import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

type MarketCapFilter = 'all' | 'large' | 'mid' | 'small';

interface StocksInMostFundItem {
    label: string;
    symbol: string;
    etf_count: number;
    total_market_value: number;
}

interface StocksInMostFundApiRow {
    symbol: string;
    etf_count: number;
    total_market_value?: number;
}

export class TraderverseMarketConcentrationRiskMap {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private chartData: StocksInMostFundItem[] = [];
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        filter: 'small' as MarketCapFilter,
        topN: 10
    };

    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        this.filters.filter = this.normalizeFilter(
            (config as any).filter ?? (this.config as any).filter ?? this.filters.filter
        );
        this.filters.topN = Number((config as any).topN ?? (this.config as any).topN ?? this.filters.topN);
        (this.config as any).filter = this.filters.filter;
        (this.config as any).topN = this.filters.topN;

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            topN: 10,
            filter: 'small'
        } as any;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private normalizeFilter(value: unknown): MarketCapFilter {
        const raw = String(value ?? 'small').trim().toLowerCase();
        if (raw === 'all' || raw === 'large' || raw === 'mid' || raw === 'small') return raw;
        return 'small';
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Market Concentration Risk Map</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="chartDv" id="stocksInMostFund">
                <div class="insiderVolumeScale">
                    <div class="insiderVolumeScaleTicks" id="stocksInMostFundScaleTicks"></div>
                    <div class="insiderVolumeScaleBar"></div>
                </div>
                <div id="chartContainer" class="chart insiderVolumeChart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        setupHeaderPin(this.shadowRoot, this.config);
    }

    private mapChartData(rows: StocksInMostFundApiRow[]): StocksInMostFundItem[] {
        if (!Array.isArray(rows) || !rows.length) return [];

        const bySymbol = new Map<string, StocksInMostFundItem>();
        rows.forEach((row) => {
            const symbol = String(row?.symbol ?? '').trim();
            if (!symbol) return;
            const etfCount = Number(row?.etf_count ?? 0);
            const marketValue = Number(row?.total_market_value ?? 0);
            const existing = bySymbol.get(symbol);
            if (!existing || etfCount > existing.etf_count) {
                bySymbol.set(symbol, {
                    label: symbol,
                    symbol,
                    etf_count: etfCount,
                    total_market_value: marketValue
                });
            }
        });

        return Array.from(bySymbol.values())
            .sort((a, b) => b.etf_count - a.etf_count)
            .slice(0, this.filters.topN || 10);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
            this.injectScaleLegendStyles();
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#stocksInMostFund') as HTMLElement;
        if (widgetBox) widgetBox.classList.add('loading');

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/etf/most-funded`,
                query: {
                    filter: this.filters.filter,
                    topN: this.filters.topN
                },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            if (json && typeof json.isSuccess === 'boolean' && !json.isSuccess) {
                throw new Error(json.message || 'API returned isSuccess: false');
            }

            const rows: StocksInMostFundApiRow[] = Array.isArray(json?.data) ? json.data : [];
            this.chartData = this.mapChartData(rows);

            if (this.chartData.length) {
                containerWrapper?.setAttribute('data-testing-id', 'stocks-in-most-fund');
            } else {
                containerWrapper?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', { filter: this.filters.filter, topN: this.filters.topN, data: this.chartData });
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.chartData = [];
            containerWrapper?.removeAttribute('data-testing-id');
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widgetBox) widgetBox.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private getNiceAxisMax(maxValue: number): number {
        if (maxValue <= 0) return 1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
        const normalized = maxValue / magnitude;
        const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
        return niceNormalized * magnitude;
    }

    private formatScaleValue(value: number): string {
        if (value >= 1000) {
            const thousands = value / 1000;
            return Number.isInteger(thousands) ? `${thousands}k` : `${thousands.toFixed(1)}k`;
        }
        return String(Math.round(value));
    }

    private getGradientColors(): string[] {
        return ['#33D17A', '#7AC74F', '#C5D63A', '#E8C41A', '#F0A01A', '#E86A1A', '#D9402A', '#B81E1E'];
    }

    private renderVolumeScaleLegend(maxValue: number): void {
        const ticksEl = this.shadowRoot.querySelector('#stocksInMostFundScaleTicks') as HTMLElement | null;
        if (!ticksEl) return;

        const steps = 5;
        const labels = Array.from({ length: steps + 1 }, (_, i) =>
            this.formatScaleValue((maxValue * i) / steps)
        );
        ticksEl.innerHTML = labels.map((label) => `<span>${label}</span>`).join('');
    }

    private updateWidgetLayout(chartHeight: number): void {
        const scaleLegendHeight = 48;
        const chartDv = this.shadowRoot.querySelector('#stocksInMostFund') as HTMLElement | null;
        const inBody = this.shadowRoot.querySelector('.inBody') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        const chartDvHeight = scaleLegendHeight + chartHeight;
        if (chartDv) {
            chartDv.style.height = `${chartDvHeight}px`;
            chartDv.style.minHeight = `${chartDvHeight}px`;
            chartDv.style.flex = '0 0 auto';
        }

        if (this.config.autosize === false) return;

        const headTop = this.shadowRoot.querySelector('.headTop') as HTMLElement | null;
        const headTopHeight = headTop?.offsetHeight ?? 56;
        const inBodyPadding = 30;
        const inBodyHeight = chartDvHeight + inBodyPadding;

        if (inBody) {
            inBody.style.height = `${inBodyHeight}px`;
            inBody.style.minHeight = `${inBodyHeight}px`;
        }
        if (widgetBox) {
            widgetBox.style.height = `${headTopHeight + inBodyHeight}px`;
        }
    }

    private injectScaleLegendStyles(): void {
        const existing = this.shadowRoot.querySelector('style[data-stocks-in-most-fund-scale]');
        if (existing) existing.remove();

        const style = document.createElement('style');
        style.setAttribute('data-stocks-in-most-fund-scale', 'true');
        style.textContent = `
            #stocksInMostFund.chartDv {
                height: auto;
                min-height: 400px;
                display: flex;
                flex-direction: column;
                flex: 0 0 auto;
                overflow: visible;
            }
            .insiderVolumeScale {
                width: 100%;
                max-width: 420px;
                padding: 4px 0 12px;
                box-sizing: border-box;
                flex: 0 0 auto;
            }
            .insiderVolumeScaleBar {
                width: 100%;
                height: 12px;
                border-radius: 6px;
                background: linear-gradient(90deg, #33D17A 0%, #7AC74F 14%, #C5D63A 28%, #E8C41A 42%, #F0A01A 57%, #E86A1A 71%, #D9402A 85%, #B81E1E 100%);
            }
            .insiderVolumeScaleTicks {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                margin-bottom: 6px;
                color: var(--charts-text-color, #9A95AC);
                font-size: var(--font-size-sm, 12px);
                line-height: 1;
            }
            .insiderVolumeScaleTicks span:first-child {
                text-align: left;
            }
            .insiderVolumeScaleTicks span:last-child {
                text-align: right;
            }
            .insiderVolumeScaleTicks span {
                flex: 1;
                text-align: center;
            }
            .insiderVolumeScaleTicks span:first-child,
            .insiderVolumeScaleTicks span:last-child {
                flex: 0 0 auto;
            }
            #stocksInMostFund .insiderVolumeChart {
                position: relative !important;
                left: auto !important;
                top: auto !important;
                width: 100%;
                height: 400px;
                flex: 0 0 auto;
                min-height: 400px;
            }
            .widgetBox:has(#stocksInMostFund) .inBody {
                height: auto;
                min-height: 480px;
                overflow: visible;
            }
        `;
        this.shadowRoot.appendChild(style);
    }

    private formatMarketValue(value: number): string {
        if (!Number.isFinite(value) || value <= 0) return '-';
        if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
        return `$${value.toFixed(0)}`;
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#stocksInMostFund') as HTMLElement;

        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const chartData = [...this.chartData];
        if (!chartData.length) {
            container.innerHTML = getNoDataHTML();
            containerWrapper?.removeAttribute('data-testing-id');
            return;
        }

        container.innerHTML = '';
        containerWrapper?.setAttribute('data-testing-id', 'stocks-in-most-fund');

        const maxEtfCount = Math.max(...chartData.map((item) => item.etf_count), 1);
        const scaleMax = this.getNiceAxisMax(maxEtfCount);
        this.renderVolumeScaleLegend(scaleMax);

        const chartHeight = 400;
        container.style.height = `${chartHeight}px`;
        container.style.minHeight = `${chartHeight}px`;
        container.style.width = '100%';
        this.updateWidgetLayout(chartHeight);

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const widgetStyles = getComputedStyle(widgetBox);
        const fontSize = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const fontSizeMd = widgetStyles.getPropertyValue('--font-size-md').trim() || '14px';
        const labelFontSize = parseInt(fontSize, 10) || 12;

        const gradientColors = this.getGradientColors();

        const treemapData = chartData.map((item) => {
            const ratio = Math.min(item.etf_count / scaleMax, 1);
            const colorIndex = Math.min(Math.floor(ratio * (gradientColors.length - 1)), gradientColors.length - 1);
            return {
                name: item.symbol,
                value: item.etf_count,
                marketValue: item.total_market_value,
                itemStyle: {
                    color: gradientColors[colorIndex]
                },
                label: {
                    show: true,
                    formatter: `{title|${item.symbol}}\n{subtitle|${item.etf_count}}`,
                    rich: {
                        title: {
                            fontSize: labelFontSize,
                            fontWeight: 700,
                            color: '#fff',
                            lineHeight: labelFontSize + 6
                        },
                        subtitle: {
                            fontSize: Math.max(labelFontSize - 2, 10),
                            color: 'rgba(255,255,255,0.85)',
                            lineHeight: Math.max(labelFontSize, 12)
                        }
                    },
                    align: 'center',
                    verticalAlign: 'middle'
                }
            };
        });

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                confine: true,
                appendToBody: false,
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const data = params.data;
                    return `
                    <div style="font-size:${fontSize}; line-height:1.45; max-width:280px;">
                        <div style="font-size:${fontSizeMd}; font-weight:600; margin-bottom:4px;">${data.name}</div>
                        <div>ETF Count: <b>${data.value}</b></div>
                        <div>Market Value: <b>${this.formatMarketValue(Number(data.marketValue))}</b></div>
                    </div>
                `;
                }
            },
            series: [
                {
                    type: 'treemap',
                    width: '100%',
                    height: '100%',
                    roam: false,
                    nodeClick: false,
                    breadcrumb: { show: false },
                    label: {
                        show: true
                    },
                    upperLabel: { show: false },
                    itemStyle: {
                        borderColor: '#000',
                        borderWidth: 2,
                        gapWidth: 2
                    },
                    emphasis: {
                        itemStyle: {
                            borderColor: '#fff',
                            borderWidth: 2
                        }
                    },
                    levels: [
                        {
                            itemStyle: {
                                borderColor: '#000',
                                borderWidth: 2,
                                gapWidth: 2
                            }
                        }
                    ],
                    data: treemapData
                }
            ],
            animation: true,
            animationDuration: 1000,
            animationEasing: 'cubicOut'
        };

        const chart = echarts.init(container, null, { renderer: 'canvas' });
        chart.setOption(option, true);
        chart.resize({ width: container.clientWidth || undefined, height: chartHeight });

        requestAnimationFrame(() => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
        });

        const resizeHandler = () => {
            chart.resize({ width: container.clientWidth || undefined, height: chartHeight });
            this.updateWidgetLayout(chartHeight);
        };
        window.addEventListener('resize', resizeHandler);
        chart.on('disposed', () => {
            window.removeEventListener('resize', resizeHandler);
        });
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }
    public destroy(): void {
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketConcentrationRiskMap: typeof TraderverseMarketConcentrationRiskMap; } }
if (typeof window !== 'undefined') { window.TraderverseMarketConcentrationRiskMap = TraderverseMarketConcentrationRiskMap; }
